import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const FLAG_EN_US = '/lang-flags/en-us.svg';
const FLAG_ES = '/lang-flags/es.svg';

function chevronDownSvg(className: string | undefined): ReactNode {
  return (
    <svg
      aria-hidden="true"
      className={className}
      height="16"
      viewBox="0 0 285 285"
      width="16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M282 76.5l-14.2-14.3a9 9 0 0 0-13.1 0L142.5 174.4 30.3 62.2a9 9 0 0 0-13.2 0L3 76.5a9 9 0 0 0 0 13.1l133 133a9 9 0 0 0 13.1 0l133-133a9 9 0 0 0 0-13z"
        fill="currentColor"
      />
    </svg>
  );
}

function langFlagImg(src: string, className: string | undefined): ReactNode {
  return <img alt="" className={className} decoding="async" src={src} width={33} />;
}

/** Floating language switcher: en → USA flag, es → Spanish flag, uppercase EN / ES codes. */
export function LanguageSwitcher(): ReactNode {
  const { i18n, t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const resolvedLang = i18n.resolvedLanguage;
  const resolved = resolvedLang !== undefined && resolvedLang.startsWith('es') ? 'es' : 'en';

  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    optionRef.current?.focus();
  }, [open, resolved]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (e: PointerEvent): void => {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return (): void => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  const select = (lng: 'en' | 'es'): void => {
    void i18n.changeLanguage(lng);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const flagSrc = resolved === 'en' ? FLAG_EN_US : FLAG_ES;

  const listboxClassName =
    'absolute right-0 bottom-full mb-2 w-[min(22rem,calc(100vw-2.5rem))] rounded-sm border border-gray-200 bg-white py-0 text-[#444] shadow-[rgba(0,0,0,0.15)_0_5px_15px] sm:w-56';

  const optionClassName =
    'flex w-full cursor-pointer items-center gap-2 px-[15px] py-2.5 text-left text-xl text-gray-800 outline-none hover:bg-[#1F4D67] hover:text-white focus-visible:ring-2 focus-visible:ring-[#1F4D67]/60 focus-visible:ring-offset-1';

  const handleOptionKeyDown =
    (lng: 'en' | 'es') =>
    (e: ReactKeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        select(lng);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        triggerRef.current?.focus();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        optionRef.current?.focus();
      }
    };

  return (
    <div ref={rootRef} className="fixed right-5 bottom-5 z-100 md:right-8 md:bottom-8">
      {open ? (
        <div
          aria-label={t('lang.listboxLabel')}
          className={listboxClassName}
          id={listboxId}
          role="listbox"
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              optionRef.current?.focus();
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              triggerRef.current?.focus();
            }
          }}
        >
          {resolved !== 'en' ? (
            <div
              ref={optionRef}
              aria-selected={false}
              className={optionClassName}
              role="option"
              tabIndex={0}
              onClick={() => {
                select('en');
              }}
              onKeyDown={handleOptionKeyDown('en')}
            >
              {langFlagImg(FLAG_EN_US, 'mr-[5px] h-auto w-[33px] shrink-0 rounded-[3px]')}
              <span className="font-sans font-normal">{t('lang.optionEnglish')}</span>
            </div>
          ) : null}
          {resolved !== 'es' ? (
            <div
              ref={optionRef}
              aria-selected={false}
              className={optionClassName}
              role="option"
              tabIndex={0}
              onClick={() => {
                select('es');
              }}
              onKeyDown={handleOptionKeyDown('es')}
            >
              {langFlagImg(FLAG_ES, 'mr-[5px] h-auto w-[33px] shrink-0 rounded-[3px]')}
              <span className="font-sans font-normal">{t('lang.optionSpanish')}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      <button
        ref={triggerRef}
        aria-controls={open ? listboxId : undefined}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t('lang.switcherAria')}
        className="flex items-center gap-1 rounded-[2px] border border-gray-200 bg-white px-[15px] py-2.5 font-sans text-xl/5 text-[#555] uppercase shadow-[rgba(0,0,0,0.15)_0_5px_15px] transition hover:border-[#1F4D67]/40 hover:shadow-lg"
        type="button"
        onClick={() => {
          setOpen((v) => !v);
        }}
        onKeyDown={(e) => {
          if (!open && e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            return;
          }
          if (open && e.key === 'ArrowDown') {
            e.preventDefault();
            optionRef.current?.focus();
          }
        }}
      >
        {langFlagImg(flagSrc, 'mr-[5px] h-auto w-[33px] shrink-0 rounded-[3px]')}
        <span className="relative top-0.5 font-bold text-[#333]">{resolved.toUpperCase()}</span>
        <span
          aria-hidden="true"
          className={`ml-0.5 inline-flex size-[15px] shrink-0 items-center justify-center text-[#666] transition-transform duration-300 ${
            open ? 'rotate-180' : ''
          }`}
        >
          {chevronDownSvg('size-[11px]')}
        </span>
      </button>
    </div>
  );
}
